import React, { useState, useEffect } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useNavigate, useLocation } from "react-router-dom";
import { MdLocalShipping, MdImage } from "react-icons/md";
import toast from "react-hot-toast";
import { tokenManager } from "utils/tokenManager";
import Loading from "components/loading";
import { invalidateRcnDeliveriesCache } from "api/rcnDeliveries";

const API_BASE = "https://usibras-web-backend.onrender.com/api";
const LOCAL_API_BASE = "https://usibras-web-backend.onrender.com/api"; // used for PUT edits per requested curl

const initialFormState = {
  rcnType: "",
  dispatchDate: "",
  truckNumber: "",
  driverName: "",
  driverContact: "",
  escortName: "",
  escortContact: "",
  warehouseLocation: "",
  warehouseLocationOther: "",
  agent: "",
  weight: "",
  quantity: "",
  moisture: "",
  weightOfArrival: "",
  analystName: "",
  status: "",
};

const DeliveryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editRecord = location.state?.editRecord;
  const isEdit = Boolean(editRecord?.id);

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (editRecord) {
      // If the stored warehouse location isn't one of the known locations,
      // switch the select to "Other" and populate warehouseLocationOther.
      const knownLocations = [
        "Techiman",
        "Nsawkaw",
        "Nkoranza",
        "Banda",
        "Wenchi",
        "Volta",
        "Nalerigu",
        "Nkrankwanta",
        "Berekum",
      ];
      const wl = editRecord.warehouse_location || "";
      const isKnown = knownLocations.includes(wl);
      setFormData({
        rcnType: editRecord.rcn_type || "",
        dispatchDate: editRecord.dispatch_date ? editRecord.dispatch_date.slice(0, 10) : "",
        truckNumber: editRecord.truck_number || "",
        driverName: editRecord.driver_name || "",
        driverContact: editRecord.driver_contact || "",
        escortName: editRecord.escort_name || "",
        escortContact: editRecord.escort_contact || editRecord.escortContact || "",
        warehouseLocation: isKnown ? wl : (wl ? "Other" : ""),
        warehouseLocationOther: isKnown ? "" : wl || "",
        agent: editRecord.agent || "",
        weight: editRecord.weight != null ? String(editRecord.weight) : "",
        quantity: editRecord.quantity != null ? String(editRecord.quantity) : "",
        moisture: editRecord.moisture != null ? String(editRecord.moisture) : "",
        weightOfArrival: editRecord.weight_of_arrival != null ? String(editRecord.weight_of_arrival) : "",
        analystName: editRecord.analyst_name || "",
        status: editRecord.status || "",
      });
    }
  }, [editRecord]);
  const [picture, setPicture] = useState(null);
  const [weighingTicket, setWeighingTicket] = useState(null);
  const [wayBill, setWayBill] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    const field = id || name;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e, setter) => {
    const file = e.target.files?.[0];
    if (file && (file.type.startsWith("image/") || file.type === "application/pdf")) {
      setter(file);
    }
  };

  const handleClearFile = (setter) => {
    setter(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const submitToastId = toast.loading(isEdit ? "Updating..." : "Submitting...");
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required. Please login again.", { id: submitToastId });
        return;
      }

      // Ensure way bill is provided when creating a record, or when
      // editing and there is no existing waybill on the record.
      const hasExistingWayBill = Boolean(
        editRecord && (
          editRecord.way_bill ||
          editRecord.wayBill ||
          editRecord.way_bill_url ||
          editRecord.wayBillUrl
        )
      );

      if (!wayBill && (!isEdit || (isEdit && !hasExistingWayBill))) {
        toast.error("Way Bill is required.", { id: submitToastId });
        setIsSubmitting(false);
        return;
      }

      const formDataToSend = new FormData();

      // For edits, the local API expects snake_case field names (per provided curl).
      // Map our form keys to the expected snake_case names when editing.
      const fieldMapSnake = {
        rcnType: "rcn_type",
        dispatchDate: "dispatch_date",
        truckNumber: "truckNumber",
        driverName: "driverName",
        driverContact: "driverContact",
        escortName: "escort_name",
        escortContact: "escort_contact",
        warehouseLocation: "warehouse_location",
        agent: "agent",
        weight: "weight",
        quantity: "quantity",
        moisture: "moisture",
        weightOfArrival: "weight_of_arrival",
        analystName: "analyst_name",
        status: "status",
      };

      const append = (key, val) => {
        if (val != null && String(val).trim() !== "") {
          formDataToSend.append(key, String(val).trim());
        }
      };

      // Compute final warehouse location (use custom when 'Other' selected)
      const finalWarehouseLocation = formData.warehouseLocation === "Other"
        ? formData.warehouseLocationOther
        : formData.warehouseLocation;

      if (isEdit) {
        // append using snake_case mapping for the edit API
        Object.entries(fieldMapSnake).forEach(([fromKey, toKey]) => {
          let val;
          if (fromKey === "warehouseLocation") {
            val = finalWarehouseLocation;
          } else {
            val = formData[fromKey];
          }
          if (val != null && String(val).trim() !== "") append(toKey, val);
        });
      } else {
        // keep previous camelCase keys for create
        append("rcnType", formData.rcnType);
        append("dispatchDate", formData.dispatchDate);
        append("truckNumber", formData.truckNumber);
        append("driverName", formData.driverName);
        append("driverContact", formData.driverContact);
        append("escortName", formData.escortName);
        append("escort_contact", formData.escortContact);
        append("warehouseLocation", finalWarehouseLocation);
        append("agent", formData.agent);
        append("weight", formData.weight);
        append("quantity", formData.quantity);
        append("moisture", formData.moisture);
        append("weightOfArrival", formData.weightOfArrival);
        append("analystName", formData.analystName);
        append("status", formData.status);
      }

      // Files: use snake_case keys for edit (way_bill, weighing_ticket), camel for create
      if (picture) formDataToSend.append(isEdit ? "picture" : "picture", picture);
      if (weighingTicket) formDataToSend.append(isEdit ? "weighing_ticket" : "weighingTicket", weighingTicket);
      if (wayBill) formDataToSend.append(isEdit ? "way_bill" : "wayBill", wayBill);

      const url = isEdit ? `${LOCAL_API_BASE}/rcn-deliveries/${editRecord.id}` : `${API_BASE}/rcn-deliveries`;
      const resp = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: authHeader },
        body: formDataToSend,
      });

      const result = await resp.json().catch(() => ({}));

      if (resp.ok) {
        toast.success(result.message || (isEdit ? "RCN delivery updated successfully" : "RCN delivery created successfully"), { id: submitToastId });
        setFormData(initialFormState);
        setPicture(null);
        setWeighingTicket(null);
        setWayBill(null);
        invalidateRcnDeliveriesCache();
        setTimeout(() => navigate("/admin/rcn-delivery/records"), 1000);
      } else {
        toast.error(result.message || result.error || (isEdit ? "Failed to update RCN delivery" : "Failed to create RCN delivery"), { id: submitToastId });
      }
    } catch (err) {
      toast.error(err.message || "Failed to create RCN delivery", { id: submitToastId });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const rcnTypes = ["Organic", "Fair Trade", "Conventional"];
  const statusOptions = ["In Transit", "Arrived at warehouse"];

  return (
    <div className="mt-3">
      {isSubmitting && <Loading variant="overlay" text={isEdit ? "Updating…" : "Submitting…"} size="lg" />}

      <h2 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
        {isEdit ? "Edit RCN Dispatch" : "RCN Dispatch Form"}
      </h2>

      <Card extra="p-6">
        <form onSubmit={handleSubmit}>
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-500 text-white">
            <MdLocalShipping className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
              Dispatch Details
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              * Indicates required field
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
              RCN Type *
            </label>
            <select
              id="rcnType"
              value={formData.rcnType}
              onChange={handleChange}
              required
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
            >
              <option value="">Select RCN type</option>
              {rcnTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <InputField
            label="Dispatch Date *"
            id="dispatchDate"
            type="date"
            value={formData.dispatchDate}
            onChange={handleChange}
            required
          />

          <InputField
            label="Truck Number/ID *"
            id="truckNumber"
            placeholder="e.g. TRK-001"
            value={formData.truckNumber}
            onChange={handleChange}
            required
          />

          <InputField
            label="Driver's Name *"
            id="driverName"
            placeholder="Enter driver's name"
            value={formData.driverName}
            onChange={handleChange}
            required
          />

          <InputField
            label="Driver's Contact"
            id="driverContact"
            type="tel"
            placeholder="e.g. 0244123456"
            value={formData.driverContact}
            onChange={handleChange}
          />

          <InputField
            label="Escort's Name"
            id="escortName"
            placeholder="Enter escort's name"
            value={formData.escortName}
            onChange={handleChange}
          />

          <InputField
            label="Escort's Contact"
            id="escortContact"
            type="tel"
            placeholder="e.g. 0244123456"
            value={formData.escortContact}
            onChange={handleChange}
          />

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
              Warehouse Location
            </label>
            <select
              id="warehouseLocation"
              value={formData.warehouseLocation}
              onChange={handleChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
            >
              <option value="">Select warehouse location</option>
              {[
                "Techiman",
                "Nsawkaw",
                "Nkoranza",
                "Banda",
                "Wenchi",
                "Volta",
                "Nalerigu",
                "Nkrankwanta",
                "Berekum",
              ].map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
            {formData.warehouseLocation === "Other" && (
              <InputField
                label="Other Warehouse Location"
                id="warehouseLocationOther"
                placeholder="Enter warehouse location"
                value={formData.warehouseLocationOther}
                onChange={handleChange}
              />
            )}
          </div>

          <InputField
            label="Agent/Association"
            id="agent"
            placeholder="Enter agent/association name"
            value={formData.agent}
            onChange={handleChange}
          />

          <InputField
            label="Departure Weight"
            id="weight"
            type="number"
            step="0.01"
            placeholder="Enter weight"
            value={formData.weight}
            onChange={handleChange}
          />

          <InputField
            label="Quantity"
            id="quantity"
            type="number"
            placeholder="Enter quantity"
            value={formData.quantity}
            onChange={handleChange}
          />

          <InputField
            label="Moisture"
            id="moisture"
            type="number"
            step="0.01"
            placeholder="e.g. 12.5 (%)"
            value={formData.moisture}
            onChange={handleChange}
          />

          <InputField
            label="Arrival Weight"
            id="weightOfArrival"
            type="number"
            step="0.01"
            placeholder="Enter arrival weight"
            value={formData.weightOfArrival}
            onChange={handleChange}
          />

          <InputField
            label="Analyst Name"
            id="analystName"
            placeholder="Enter analyst name"
            value={formData.analystName}
            onChange={handleChange}
          />

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
            >
              <option value="">Select status</option>
              {statusOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
              Weighing Ticket
            </label>
            <div className="flex items-center gap-4">
              <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:border-navy-600 dark:bg-navy-800/50 dark:hover:border-brand-500 dark:hover:bg-navy-800">
                <MdImage className="h-8 w-8 text-gray-400 dark:text-navy-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {weighingTicket ? weighingTicket.name : "Upload weighing ticket"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, setWeighingTicket)}
                  className="hidden"
                />
              </label>
              {weighingTicket && (
                <div className="flex items-center gap-2">
                  {weighingTicket.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(weighingTicket)}
                      alt="Weighing ticket"
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {weighingTicket.name}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleClearFile(setWeighingTicket)}
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Way Bill *
              </label>
            <div className="flex items-center gap-4">
              <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:border-navy-600 dark:bg-navy-800/50 dark:hover:border-brand-500 dark:hover:bg-navy-800">
                <MdImage className="h-8 w-8 text-gray-400 dark:text-navy-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {wayBill ? wayBill.name : "Upload way bill"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, setWayBill)}
                  className="hidden"
                  required={!isEdit || !Boolean(
                    editRecord && (
                      editRecord.way_bill ||
                      editRecord.wayBill ||
                      editRecord.way_bill_url ||
                      editRecord.wayBillUrl
                    )
                  )}
                />
              </label>
              {wayBill && (
                <div className="flex items-center gap-2">
                  {wayBill.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(wayBill)}
                      alt="Way bill"
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {wayBill.name}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleClearFile(setWayBill)}
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col">
            <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
              Picture Upload
            </label>
            <div className="flex items-center gap-4">
              <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:border-navy-600 dark:bg-navy-800/50 dark:hover:border-brand-500 dark:hover:bg-navy-800">
                <MdImage className="h-8 w-8 text-gray-400 dark:text-navy-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {picture ? picture.name : "Choose image or drag and drop"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setPicture)}
                  className="hidden"
                />
              </label>
              {picture && (
                <div className="flex items-center gap-2">
                  <img
                    src={URL.createObjectURL(picture)}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleClearFile(setPicture)}
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/rcn-delivery/records")}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-brand-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {isSubmitting ? (isEdit ? "Updating…" : "Saving…") : isEdit ? "Update" : "Submit"}
          </button>
        </div>
        </form>
      </Card>
    </div>
  );
};

export default DeliveryForm;
